import flatten from "lodash/flatten";
import { Post, PostStates } from "./types";
import {
  editDraft,
  editQueuedPost,
  editScheduledPost,
  moveDraftToSchedule,
  moveDraftToQueue,
  moveQueueToDraft,
  moveQueueToSchedule,
  moveScheduleToDraft,
  moveScheduledPostToQueue,
  createScheduledPost,
  createDraft,
  createQueuedPost,
  editDraftValidation,
} from "api/post";
import type { PostCreationSource } from "utils/post-creation-source";

export const savePost =
  (
    posts: Post | Post[],
    previousPost: Post,
    options?: { creationSource?: PostCreationSource }
  ) =>
  async () => {
    // todo: when we put all posts to the store we should get previous from it
    // const previousPost = parsePost(previousPostData);
    if (Array.isArray(posts)) {
      const { editPost, newPosts }: { editPost?: Post; newPosts: Post[] } =
        posts.reduce(
          (acc: { editPost?: Post; newPosts: Post[] }, post) => {
            // eslint-disable-next-line unicorn/no-negated-condition
            if (!post._id) {
              acc.newPosts.push(post);
            } else {
              acc.editPost = post;
            }

            return acc;
          },
          { editPost: undefined, newPosts: [] }
        );
      let updatedIds: string[] = [];
      if (editPost) {
        const creationSource = resolveQueueCreationSource(
          editPost,
          previousPost,
          options?.creationSource
        );
        updatedIds = await PostEditingMap[previousPost.state][editPost.state](
          editPost,
          previousPost,
          creationSource
        );
      }

      const createdPostResult = await Promise.allSettled(
        newPosts.map(async (post) => {
          const creationSource = resolveQueueCreationSource(
            post,
            previousPost,
            options?.creationSource
          );
          const rs = await PostCreatingMap[post.state](post, creationSource);
          return {
            state: post.state,
            data: rs,
          };
        })
      );

      return {
        ids: [
          ...createdPostResult.flatMap((c) =>
            c.status === "fulfilled" && c.value.data ? c.value.data : []
          ),
          ...updatedIds,
        ],
      };
    }

    const creationSource = resolveQueueCreationSource(
      posts,
      previousPost,
      options?.creationSource
    );
    const rs = await PostEditingMap[previousPost.state][posts.state](
      posts,
      previousPost,
      creationSource
    );

    return {
      ids: rs,
    };
  };

const PostCreatingMap: Record<
  PostStates,
  (post: Post, creationSource?: PostCreationSource) => Promise<string[]>
> = {
  [PostStates.draft]: createDraft,
  [PostStates.schedule]: createScheduledPost,
  [PostStates.queue]: createQueuedPost,
  [PostStates.archived]: async () => {
    return [];
  }, // archived draft is not creatable
} as const;

type MakePostFunc = (
  post: Post,
  previousPost?: Post,
  creationSource?: PostCreationSource
) => Promise<string[]>;

interface MakePostService {
  [PostStates.draft]: MakePostFunc;
  [PostStates.schedule]: MakePostFunc;
  [PostStates.queue]: MakePostFunc;
  [PostStates.archived]: MakePostFunc;
}

const PostEditingMap = {
  [PostStates.draft]: {
    [PostStates.draft]: async (post: Post) => {
      await editDraft(post);
      return [post._id];
    },
    [PostStates.schedule]: async (post: Post) => {
      await editDraft(post);
      try {
        const rs = await moveDraftToSchedule(post);
        return flatten(rs);
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        if (message.includes("Could not find post")) {
          throw new Error(
            "This draft is no longer available. Please refresh the page to see the latest state of this draft and try again.",
            { cause: error }
          );
        }
        throw error;
      }
    },
    [PostStates.queue]: async (
      post: Post,
      _previousPost: Post | undefined,
      creationSource: PostCreationSource | undefined
    ) => {
      await editDraft(post);
      return await moveDraftToQueue(post._id, creationSource);
    },
    [PostStates.archived]: async () => {
      return [];
    }, // archived draft is not editable
  } satisfies MakePostService as MakePostService,
  [PostStates.schedule]: {
    [PostStates.schedule]: editScheduledPost,
    [PostStates.draft]: async (post: Post) => {
      await editDraftValidation(post);
      const newId = await moveScheduleToDraft(post._id);
      await editDraft({ ...post, _id: newId }, { validate: false });
      return [newId];
    },
    [PostStates.queue]: async (
      post: Post,
      _previousPost: Post | undefined,
      creationSource: PostCreationSource | undefined
    ) => {
      const ids = await moveScheduledPostToQueue(
        post._id,
        creationSource,
        post.postStatusKey
      );
      return await editQueuedPost({ ...post, _id: ids[0] });
    },
    [PostStates.archived]: async () => {
      return [];
    }, // archived draft is not editable
  } satisfies MakePostService as MakePostService,
  [PostStates.archived]: {
    [PostStates.schedule]: (post: Post, previousPost: Post | undefined) => {
      if (previousPost?.isSuccess) {
        return PostCreatingMap[post.state](post);
      }

      return editScheduledPost(post);
    },
    [PostStates.draft]: async (post: Post, previousPost: Post | undefined) => {
      if (previousPost?.isSuccess) {
        return PostCreatingMap[post.state](post);
      }

      await editDraftValidation(post);
      const newId = await moveScheduleToDraft(post._id);
      await editDraft({ ...post, _id: newId }, { validate: false });
      return [newId];
    },
    [PostStates.queue]: async (
      post: Post,
      previousPost: Post | undefined,
      creationSource: PostCreationSource | undefined
    ) => {
      if (previousPost?.isSuccess) {
        return PostCreatingMap[post.state](post, creationSource);
      }

      const ids = await moveScheduledPostToQueue(
        post._id,
        creationSource,
        post.postStatusKey
      );
      return await editQueuedPost({ ...post, _id: ids[0] });
    },
    [PostStates.archived]: async () => {
      return [];
    }, // archived draft is not editable
  } satisfies MakePostService as MakePostService,
  [PostStates.queue]: {
    [PostStates.queue]: editQueuedPost,
    [PostStates.draft]: async (post: Post) => {
      await editDraftValidation(post);
      const newId = await moveQueueToDraft(post._id!);
      await editDraft({ ...post, _id: newId }, { validate: false });
      return [newId];
    },
    [PostStates.schedule]: async (post: Post) => {
      const rs = await moveQueueToSchedule(post);
      return rs.data;
    },
    [PostStates.archived]: async () => {
      return [];
    }, // archived draft is not editable
  } satisfies MakePostService as MakePostService,
} as const;

function resolveQueueCreationSource(
  post: Post,
  previousPost: Post,
  requestedSource?: PostCreationSource
): PostCreationSource | undefined {
  if (
    post.state !== PostStates.queue ||
    previousPost.state === PostStates.queue
  ) {
    return undefined;
  }

  if (requestedSource) {
    return requestedSource;
  }

  return previousPost.state === PostStates.archived && previousPost.isSuccess
    ? "composer"
    : "single-conversion";
}
